import _slicedToArray from "/home/skynet/Zhifan/repos/visualize-rotations/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { Cast, util } from '@tensorflow/tfjs-core';
import { createSimpleBinaryKernelImpl } from '../utils/binary_impl';
import { zeros } from '../utils/zeros_impl';
import { complex } from './Complex';
import { identity } from './Identity';
import { real } from './Real';
export function castImpl(values, shape, inputType, dtype) {
  if (dtype === 'int32') {
    var resultValues = Int32Array.from(values);
    return [shape, 'int32', resultValues];
  }
  if (dtype === 'bool') {
    // This is essentially the result of notEqual(x, 0). We avoid using
    // kernel notEqual to avoid circular dependency, i.e. binary_utils ->
    // cast -> notEqual -> binary_utils.
    var zero = util.toTypedArray([0], inputType);
    var _createSimpleBinaryKe = createSimpleBinaryKernelImpl(function (a, b) {
        return a !== b ? 1 : 0;
      })(shape, [], values, zero, 'bool'),
      _createSimpleBinaryKe2 = _slicedToArray(_createSimpleBinaryKe, 2),
      resultData = _createSimpleBinaryKe2[0],
      resultShape = _createSimpleBinaryKe2[1];
    return [resultShape, 'bool', resultData];
  }
  throw new Error("Error in Cast: failed to cast ".concat(inputType, " to ").concat(dtype));
}
export function cast(args) {
  var inputs = args.inputs,
    backend = args.backend,
    attrs = args.attrs;
  var x = inputs.x;
  var dtype = attrs.dtype; // Casting to complex64.
  if (dtype === 'complex64') {
    if (x.dtype === 'complex64') {
      return identity({
        inputs: {
          x: x
        },
        backend: backend
      });
    }
    var zerosTensorInfo = zeros(backend, x.shape, x.dtype);
    var floatX = cast({
      inputs: {
        x: x
      },
      backend: backend,
      attrs: {
        dtype: 'float32'
      }
    });
    var result = complex({
      inputs: {
        real: floatX,
        imag: zerosTensorInfo
      },
      backend: backend
    });
    backend.disposeIntermediateTensorInfo(zerosTensorInfo);
    backend.disposeIntermediateTensorInfo(floatX);
    return result;
  }
  // Casting from complex64
  if (x.dtype === 'complex64') {
    var realPart = real({
      inputs: {
        input: x
      },
      backend: backend
    });
    var _result = cast({
      inputs: {
        x: realPart
      },
      backend: backend,
      attrs: {
        dtype: dtype
      }
    });
    backend.disposeIntermediateTensorInfo(realPart);
    return _result;
  }
  if (!util.hasEncodingLoss(x.dtype, dtype)) {
    // We don't change the underlying data, since we cast to higher
    // precision.
    var _result2 = identity({
      inputs: {
        x: x
      },
      backend: backend
    });
    return {
      dataId: _result2.dataId,
      shape: _result2.shape,
      dtype: dtype
    };
  }
  var values = backend.data.get(x.dataId).values;
  var _castImpl = castImpl(values, x.shape, x.dtype, dtype),
    _castImpl2 = _slicedToArray(_castImpl, 3),
    resultShape = _castImpl2[0],
    resultType = _castImpl2[1],
    resultData = _castImpl2[2];
  return backend.makeTensorInfo(resultShape, resultType, resultData);
}
export var castConfig = {
  kernelName: Cast,
  backendName: 'cpu',
  kernelFunc: cast
};